import AbstractRequest from '../AbstractRequest';
import { ApiMethod } from '../constants';
import {
  ApiResponseBusinessVideoReels,
  ApiResponseGetBusinessPackage,
  ApiResponseGetBusinessPackages,
  ApiResponseRequestToJoin,
} from '../types/business/businessPackage';

export class BusinessPackageRequest extends AbstractRequest {
  get apiResource(): string {
    return 'business/package';
  }

  get dynamicIds(): Record<string, string> {
    return {
      id: '{id}',
      experienceSlug: '{experienceSlug}',
    };
  }

  get routes(): Record<string, string> {
    return {
      GET_BUSINESS_PACKAGES: `${this.apiRoute}`,
      GET_BUSINESS_BY_SLUG: `${this.apiRoute}/${this.dynamicIds.experienceSlug}`,
      GET_BUSINESS_FAVORITES: `${this.apiRoute}/favorites`,
      IS_REQUESTABLE: `${this.apiRoute}/${this.dynamicIds.id}/is-requestable`,
      FAVOURITE: `${this.apiRoute}/${this.dynamicIds.id}/favorite`,
      UNFAVOURITE: `${this.apiRoute}/${this.dynamicIds.id}/unfavorite`,
      REQUEST_TO_JOIN: `${this.apiRoute}/${this.dynamicIds.id}/request-to-join`,
      CHECK_IF_FAVORITED: `${this.apiRoute}/${this.dynamicIds.id}/check-if-favorited`,
      MIND_FEED: `${this.apiRoute}/mind-feed-videos`,
      MIND_FEED_BY_EXPERIENCE: `${this.apiRoute}/${this.dynamicIds.id}/video-reel`,
      MIND_FEED_FAVORITES: `${this.apiRoute}/favorite-mind-feed-videos`,
    };
  }

  async getBusinessPackages(queryOptions: {
    limit: number;
    offset: number;
    domain?: string;
    searchText?: string;
  }): Promise<ApiResponseGetBusinessPackages> {
    const queryParams = [
      { key: 'limit', value: `${queryOptions.limit}` },
      { key: 'offset', value: `${queryOptions.offset}` },
    ];
    if (queryOptions.domain) {
      queryParams.push({ key: 'domain', value: queryOptions.domain });
    }
    if (queryOptions.searchText) {
      queryParams.push({ key: 'searchText', value: queryOptions.searchText });
    }
    const result = await this.request(
      ApiMethod.GET,
      this.routes.GET_BUSINESS_PACKAGES + this.buildQueryParams(queryParams)
    );

    return result.data;
  }

  async getMindFeedVideos(queryOptions: {
    limit: number;
    offset: number;
    seed: string;
  }): Promise<ApiResponseGetBusinessPackages> {
    const queryParams = [
      { key: 'limit', value: `${queryOptions.limit}` },
      { key: 'offset', value: `${queryOptions.offset}` },
      { key: 'seed', value: queryOptions.seed },
    ];

    const result = await this.request(
      ApiMethod.GET,
      this.routes.MIND_FEED + this.buildQueryParams(queryParams)
    );

    return result.data;
  }

  async getFavoritesMindFeedVideos(queryOptions: {
    limit: number;
    offset: number;
    seed: string;
  }): Promise<ApiResponseGetBusinessPackages> {
    const queryParams = [
      { key: 'limit', value: `${queryOptions.limit}` },
      { key: 'offset', value: `${queryOptions.offset}` },
      { key: 'seed', value: queryOptions.seed },
    ];

    const result = await this.request(
      ApiMethod.GET,
      this.routes.MIND_FEED_FAVORITES + this.buildQueryParams(queryParams)
    );

    return result.data;
  }

  async getMindFeedVideosByExperience(queryOptions: {
    limit: number;
    offset: number;
    id: string;
  }): Promise<ApiResponseBusinessVideoReels> {
    const queryParams = [
      { key: 'limit', value: `${queryOptions.limit}` },
      { key: 'offset', value: `${queryOptions.offset}` },
    ];

    const result = await this.request(
      ApiMethod.GET,
      this.routes.MIND_FEED_BY_EXPERIENCE!.replace(
        this.dynamicIds.id!,
        queryOptions.id
      ) + this.buildQueryParams(queryParams)
    );

    return result.data;
  }

  async getBusinessPackageBySlug(
    experienceSlug: string
  ): Promise<ApiResponseGetBusinessPackage> {
    const result = await this.request(
      ApiMethod.GET,
      this.routes.GET_BUSINESS_BY_SLUG!.replace(
        this.dynamicIds.experienceSlug!,
        experienceSlug
      )
    );

    return { experience: result.data };
  }

  async getBusinessFavorites(
    limit = 10,
    offset = 0
  ): Promise<ApiResponseGetBusinessPackages> {
    const queryParams = [
      { key: 'limit', value: `${limit}` },
      { key: 'offset', value: `${offset}` },
    ];
    const result = await this.request(
      ApiMethod.GET,
      this.routes.GET_BUSINESS_FAVORITES + this.buildQueryParams(queryParams)
    );

    return result.data;
  }

  async isBusinessPackageRequestable(id: string): Promise<boolean> {
    const result = await this.request(
      ApiMethod.GET,
      this.routes.IS_REQUESTABLE!.replace(this.dynamicIds.id!, id)
    );

    return result.data;
  }

  async favouriteBusinessPackage(id: string): Promise<void> {
    await this.request(
      ApiMethod.POST,
      this.routes.FAVOURITE!.replace(this.dynamicIds.id!, id)
    );
  }

  async unfavouriteBusinessPackage(id: string): Promise<void> {
    await this.request(
      ApiMethod.POST,
      this.routes.UNFAVOURITE!.replace(this.dynamicIds.id!, id)
    );
  }

  async requestToJoinBusinessPackage(
    id: string
  ): Promise<ApiResponseRequestToJoin> {
    const result = await this.request(
      ApiMethod.POST,
      this.routes.REQUEST_TO_JOIN!.replace(this.dynamicIds.id!, id)
    );

    return result.data;
  }

  async checkIfFavorited(id: string): Promise<boolean> {
    const result = await this.request(
      ApiMethod.GET,
      this.routes.CHECK_IF_FAVORITED!.replace(this.dynamicIds.id!, id)
    );

    return result.data;
  }
}

export const businessPackageRequest = new BusinessPackageRequest();
